import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import ContractorFormModal from './ContractorFormModal';
import ContractorDetailModal from './ContractorDetailModal';
import Swal from 'sweetalert2'; // SweetAlert2 임포트
import '../App.css';
import { useAuth } from '../context/AuthContext';
import './ContractorManagementPage.css';

function ContractorManagementPage() {
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [formModalMode, setFormModalMode] = useState('add'); // 'add' or 'edit'

  const fetchContractors = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/contractors');
      setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  }, []);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  const filteredContractors = contractors.filter(contractor =>
    contractor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contractor.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contractor.representative.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (contractor.remarks && contractor.remarks.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddClick = () => {
    setFormModalMode('add');
    setSelectedContractor(null); // Clear any previously selected contractor
    setIsFormModalOpen(true);
  };

  const handleEditClick = (contractor) => {
    setFormModalMode('edit');
    setSelectedContractor(contractor);
    setIsFormModalOpen(true);
  };

  const handleDetailClick = (contractor) => {
    setSelectedContractor(contractor);
    setIsDetailModalOpen(true);
  };

  const handleSaveContractor = async (contractorData) => {
    try {
      if (formModalMode === 'add') {
        await axiosInstance.post('/contractors', contractorData);
        Swal.fire({
          icon: 'success',
          title: '성공',
          text: '시공사가 성공적으로 등록되었습니다.',
        });
      } else {
        await axiosInstance.put(`/contractors/${contractorData.id}`, contractorData);
        Swal.fire({
          icon: 'success',
          title: '성공',
          text: '시공사 정보가 성공적으로 업데이트되었습니다.',
        });
      }
      fetchContractors();
      setIsFormModalOpen(false);
    } catch (error) {
      console.error('Error saving contractor:', error.response ? error.response.data : error.message);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: '시공사 저장에 실패했습니다.' + (error.response ? ': ' + error.response.data : ''),
      });
    }
  };

  const handleDeleteContractor = async (id) => {
    const result = await Swal.fire({
      title: '정말로 이 시공사를 삭제하시겠습니까?',
      text: "이 시공사를 삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/contractors/${id}`);
        Swal.fire(
          '삭제됨!',
          '시공사가 성공적으로 삭제되었습니다.',
          'success'
        );
        fetchContractors();
      } catch (error) {
        console.error('Error deleting contractor:', error.response ? error.response.data : error.message);
        Swal.fire(
          '오류!',
          '시공사 삭제에 실패했습니다.' + (error.response ? ': ' + error.response.data : ''),
          'error'
        );
      }
    }
  };

  return (
    <div className="container">
      <br/>
      <h2>시공사 관리</h2>
      <br/><br/><br/>
      <div className="page-actions">
        <input
          type="text"
          placeholder="시공사 검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="button-group">
          {user && user.role === 'ADMIN' && (
            <button onClick={handleAddClick} className="register-button">
              시공사 추가
            </button>
          )}
        </div>
      </div>
      <br/><br/><br/>
      {filteredContractors.length > 0 ? (
        <table className="contractor-table">
          <thead>
            <tr>
              <th className="col-name">시공사명</th>
              <th className="col-address">주소</th>
              <th className="col-representative">대표</th>
              {user && user.role === 'ADMIN' && <th className="col-actions">관리</th>}
            </tr>
          </thead>
          <tbody>
            {filteredContractors.map((contractor) => (
              <tr key={contractor.id}>
                <td className="col-name" onClick={() => handleDetailClick(contractor)} style={{ cursor: 'pointer' }}>{contractor.name}</td>
                <td className="col-address" onClick={() => handleDetailClick(contractor)} style={{ cursor: 'pointer' }}>{contractor.address}</td>
                <td className="col-representative" onClick={() => handleDetailClick(contractor)} style={{ cursor: 'pointer' }}>{contractor.representative}</td>
                {user && user.role === 'ADMIN' && (
                  <td className="col-actions">
                    <div className="contractor-actions-container"> {/* New container for buttons */}
                      <button onClick={() => handleEditClick(contractor)} className="contractor-edit-button">수정</button>
                      <button onClick={() => handleDeleteContractor(contractor.id)} className="contractor-delete-button">삭제</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>등록된 시공사가 없습니다.</p>
      )}

      {isFormModalOpen && (
        <ContractorFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSave={handleSaveContractor}
          mode={formModalMode}
          initialData={selectedContractor}
        />
      )}

      {isDetailModalOpen && (
        <ContractorDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          contractor={selectedContractor}
          onEdit={handleEditClick}
          onDelete={handleDeleteContractor}
        />
      )}
    </div>
  );
}

export default ContractorManagementPage;
